/**
 * Réglages portail étudiant.
 * - Confidentialité : masquer matricule, invisible
 * - Chat : qui peut m'écrire
 * - Notifications : silencieuses
 * - Mot de passe : changement avec vérification ancien
 * - Appareils : déconnecter tous les appareils (push)
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Lock, Eye, EyeOff, MessageSquare, BellOff, Smartphone, Loader2,
  Check, AlertCircle, Shield, KeyRound, Fingerprint, Trash2, Cake,
} from 'lucide-react';
import { PortailLayout } from '../PortailLayout';
import { portailApi } from '../services/portailApi';
import {
  isWebAuthnSupported, isPlatformAuthenticatorAvailable,
  decodeCreationOptions, encodeAttestationCredential,
} from '../services/webauthn';

type Prefs = {
  pref_masquer_matricule: boolean;
  pref_invisible: boolean;
  pref_qui_peut_ecrire: 'tous' | 'ma_classe' | 'personne';
  pref_notif_silencieuses: boolean;
  pref_anniv_public: boolean;
  pref_age_public: boolean;
};

export default function Reglages() {
  const navigate = useNavigate();
  const [prefs, setPrefs] = useState<Prefs | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    portailApi.getPreferences()
      .then((r) => { if (mounted) setPrefs(r); })
      .catch((e: any) => {
        if (e?.status === 401) { portailApi.clearToken(); navigate('/portail/login', { replace: true }); }
        else if (mounted) setError(e?.message || 'Erreur');
      })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [navigate]);

  const updatePref = async <K extends keyof Prefs>(key: K, value: Prefs[K]) => {
    if (!prefs) return;
    setSavingKey(key as string);
    setFeedback(null);
    try {
      await portailApi.setPreferences({ [key]: value } as any);
      setPrefs({ ...prefs, [key]: value });
      setFeedback('Préférence enregistrée');
      setTimeout(() => setFeedback(null), 2000);
    } catch (e: any) {
      setError(e?.message || 'Erreur');
    } finally {
      setSavingKey(null);
    }
  };

  if (loading) {
    return (
      <PortailLayout showHeader title="Réglages" fullWidth>
        <div className="text-center py-12">
          <Loader2 className="inline-block animate-spin text-blue-400" size={28} />
        </div>
      </PortailLayout>
    );
  }

  if (error || !prefs) {
    return (
      <PortailLayout showHeader title="Réglages" fullWidth>
        <div className="bg-red-900/30 border border-red-800 rounded-lg p-3 text-sm text-red-300">
          {error || 'Impossible de charger les réglages'}
        </div>
      </PortailLayout>
    );
  }

  return (
    <PortailLayout showHeader title="Réglages" fullWidth>
      <div className="space-y-3">
        <button
          onClick={() => navigate('/portail/accueil')}
          className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-gray-200"
        >
          <ArrowLeft size={16} /> Retour
        </button>

        {feedback && (
          <div className="bg-green-900/30 border border-green-800 rounded-lg px-3 py-2 text-xs text-green-300 inline-flex items-center gap-2">
            <Check size={12} /> {feedback}
          </div>
        )}

        {/* Confidentialité */}
        <Section icon={<Shield size={16} />} title="Confidentialité">
          <ToggleRow
            icon={<EyeOff size={14} />}
            label="Masquer mon matricule"
            description="Les autres étudiants ne verront pas votre matricule dans le chat."
            checked={prefs.pref_masquer_matricule}
            saving={savingKey === 'pref_masquer_matricule'}
            onChange={(v) => updatePref('pref_masquer_matricule', v)}
          />
          <ToggleRow
            icon={<Eye size={14} />}
            label="Mode invisible"
            description="Vous n'apparaissez pas dans la liste des étudiants en ligne, même connecté."
            checked={prefs.pref_invisible}
            saving={savingKey === 'pref_invisible'}
            onChange={(v) => updatePref('pref_invisible', v)}
          />
          <ToggleRow
            icon={<Cake size={14} />}
            label="Anniversaire visible"
            description="Si activé (défaut), votre anniversaire est visible des autres étudiants et de la scolarité qui peuvent vous souhaiter joyeux anniversaire. Si désactivé, vous seul recevez la notification système le jour J."
            checked={prefs.pref_anniv_public}
            saving={savingKey === 'pref_anniv_public'}
            onChange={(v) => updatePref('pref_anniv_public', v)}
          />
        </Section>

        {/* Chat */}
        <Section icon={<MessageSquare size={16} />} title="Messagerie">
          <div className="px-3 py-3">
            <div className="flex items-start gap-2 mb-2">
              <MessageSquare size={14} className="text-gray-400 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <div className="text-sm font-medium text-gray-100">Qui peut m'envoyer un message ?</div>
                <div className="text-[11px] text-gray-500 mt-0.5">
                  Limitez qui peut démarrer une conversation avec vous.
                </div>
              </div>
            </div>
            <div className="space-y-1.5 ml-6">
              {[
                { v: 'tous', label: 'Tout le monde', desc: 'N\'importe quel étudiant de l\'institut' },
                { v: 'ma_classe', label: 'Camarades de ma classe', desc: 'Uniquement les étudiants de votre classe' },
                { v: 'personne', label: 'Personne', desc: 'Désactiver complètement le chat entrant' },
              ].map((opt) => (
                <label
                  key={opt.v}
                  className={`flex items-start gap-2 p-2 rounded border cursor-pointer transition-colors ${
                    prefs.pref_qui_peut_ecrire === opt.v
                      ? 'bg-blue-900/30 border-blue-700'
                      : 'bg-gray-950 border-gray-800 hover:border-gray-700'
                  }`}
                >
                  <input
                    type="radio"
                    name="qui_peut_ecrire"
                    checked={prefs.pref_qui_peut_ecrire === opt.v}
                    onChange={() => updatePref('pref_qui_peut_ecrire', opt.v as any)}
                    disabled={savingKey === 'pref_qui_peut_ecrire'}
                    className="mt-0.5"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-medium text-gray-100">{opt.label}</div>
                    <div className="text-[10px] text-gray-500">{opt.desc}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>
        </Section>

        {/* Notifications */}
        <Section icon={<BellOff size={16} />} title="Notifications">
          <ToggleRow
            icon={<BellOff size={14} />}
            label="Notifications silencieuses"
            description="Désactive complètement les notifications push (annonces, messages, alertes)."
            checked={prefs.pref_notif_silencieuses}
            saving={savingKey === 'pref_notif_silencieuses'}
            onChange={(v) => updatePref('pref_notif_silencieuses', v)}
          />
        </Section>

        {/* Anniversaire */}
        <Section icon={<Cake size={16} />} title="Anniversaire">
          <ToggleRow
            icon={<Cake size={14} />}
            label="Mon anniversaire est public"
            description="Si activé, les autres étudiants et la scolarité voient votre anniversaire le jour J et peuvent vous souhaiter. Si désactivé, vous serez le seul à recevoir la notification système."
            checked={prefs.pref_anniv_public}
            saving={savingKey === 'pref_anniv_public'}
            onChange={(v) => updatePref('pref_anniv_public', v)}
          />
          <ToggleRow
            icon={<Cake size={14} />}
            label="Afficher mon âge"
            description="Par défaut, votre âge est masqué pour des raisons de confidentialité. Activez si vous voulez qu'il apparaisse sur votre carte d'anniversaire."
            checked={prefs.pref_age_public}
            saving={savingKey === 'pref_age_public'}
            onChange={(v) => updatePref('pref_age_public', v)}
          />
        </Section>

        {/* Mot de passe + Question secrète */}
        <Section icon={<KeyRound size={16} />} title="Sécurité">
          <QuestionSecurite onSaved={() => setFeedback('Question de sécurité enregistrée')} />
          <ChangerMotDePasse onSaved={() => setFeedback('Mot de passe modifié')} />
          <DeconnecterAppareils onDone={(n) => setFeedback(`${n} appareil(s) déconnecté(s)`)} />
        </Section>

        {/* Empreinte / biométrie */}
        <Section icon={<Fingerprint size={16} />} title="Connexion biométrique">
          <AppareilsSecurises onFeedback={(t) => setFeedback(t)} />
        </Section>

        {/* Footer */}
        <div className="pt-3 text-center text-[10px] text-gray-600 inline-flex items-center justify-center gap-1 w-full">
          <Lock size={10} /> Données chiffrées en transit (HTTPS)
        </div>
      </div>
    </PortailLayout>
  );
}

// ─── UI helpers ─────────────────────────────────────────────────

const Section: React.FC<{
  icon: React.ReactNode; title: string; children: React.ReactNode;
}> = ({ icon, title, children }) => (
  <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
    <div className="px-3 py-2 border-b border-gray-800 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-gray-400">
      {icon} {title}
    </div>
    <div className="divide-y divide-gray-800">{children}</div>
  </div>
);

const ToggleRow: React.FC<{
  icon: React.ReactNode;
  label: string;
  description: string;
  checked: boolean;
  saving: boolean;
  onChange: (v: boolean) => void;
}> = ({ icon, label, description, checked, saving, onChange }) => (
  <label className="flex items-start gap-3 px-3 py-3 cursor-pointer hover:bg-gray-800/30">
    <span className="text-gray-400 mt-0.5">{icon}</span>
    <div className="flex-1 min-w-0">
      <div className="text-sm font-medium text-gray-100">{label}</div>
      <div className="text-[11px] text-gray-500 mt-0.5">{description}</div>
    </div>
    <div className="flex-shrink-0 mt-0.5">
      {saving ? (
        <Loader2 size={16} className="animate-spin text-blue-400" />
      ) : (
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          className="w-4 h-4 accent-blue-600"
        />
      )}
    </div>
  </label>
);

const ChangerMotDePasse: React.FC<{ onSaved: () => void }> = ({ onSaved }) => {
  const [open, setOpen] = useState(false);
  const [ancien, setAncien] = useState('');
  const [nouveau, setNouveau] = useState('');
  const [confirme, setConfirme] = useState('');
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async () => {
    setErr(null);
    if (nouveau.length < 6) { setErr('Nouveau mot de passe trop court (6 min)'); return; }
    if (nouveau !== confirme) { setErr('Les deux nouveaux mots de passe ne correspondent pas'); return; }
    setLoading(true);
    try {
      await portailApi.changerMotDePasse(ancien, nouveau);
      setAncien(''); setNouveau(''); setConfirme('');
      setOpen(false);
      onSaved();
    } catch (e: any) {
      setErr(e?.data?.detail || e?.message || 'Erreur');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="px-3 py-3">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-3 text-left"
      >
        <KeyRound size={14} className="text-gray-400" />
        <div className="flex-1">
          <div className="text-sm font-medium text-gray-100">Changer mon mot de passe</div>
          <div className="text-[11px] text-gray-500">Vérification de l'ancien mot de passe requise.</div>
        </div>
        <span className="text-xs text-blue-400">{open ? 'Fermer' : 'Ouvrir'}</span>
      </button>
      {open && (
        <div className="mt-3 ml-6 space-y-2">
          <input
            type="password"
            placeholder="Ancien mot de passe"
            value={ancien}
            onChange={(e) => setAncien(e.target.value)}
            className="w-full px-3 py-2 bg-gray-950 border border-gray-700 rounded text-sm text-gray-100 focus:outline-none focus:border-blue-500"
          />
          <input
            type="password"
            placeholder="Nouveau mot de passe (8 caractères min.)"
            value={nouveau}
            onChange={(e) => setNouveau(e.target.value)}
            className="w-full px-3 py-2 bg-gray-950 border border-gray-700 rounded text-sm text-gray-100 focus:outline-none focus:border-blue-500"
          />
          <input
            type="password"
            placeholder="Confirmer le nouveau mot de passe"
            value={confirme}
            onChange={(e) => setConfirme(e.target.value)}
            className="w-full px-3 py-2 bg-gray-950 border border-gray-700 rounded text-sm text-gray-100 focus:outline-none focus:border-blue-500"
          />
          {err && (
            <div className="text-xs text-red-400 inline-flex items-center gap-1">
              <AlertCircle size={11} /> {err}
            </div>
          )}
          <button
            onClick={submit}
            disabled={loading || !ancien || !nouveau || !confirme}
            className="w-full px-3 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium rounded inline-flex items-center justify-center gap-2"
          >
            {loading ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
            Enregistrer
          </button>
        </div>
      )}
    </div>
  );
};

const DeconnecterAppareils: React.FC<{ onDone: (nb: number) => void }> = ({ onDone }) => {
  const [loading, setLoading] = useState(false);

  const click = async () => {
    if (!window.confirm('Désactiver les notifications sur tous vos appareils ? Vous devrez les réactiver appareil par appareil.')) return;
    setLoading(true);
    try {
      const r = await portailApi.deconnecterAppareils();
      onDone(r.nb_desactives);
    } catch (e: any) {
      alert(e?.message || 'Erreur');
    } finally {
      setLoading(false);
    }
  };

  return (
    <button
      onClick={click}
      disabled={loading}
      className="w-full flex items-center gap-3 px-3 py-3 text-left hover:bg-gray-800/30"
    >
      <Smartphone size={14} className="text-gray-400" />
      <div className="flex-1">
        <div className="text-sm font-medium text-gray-100">Déconnecter mes appareils</div>
        <div className="text-[11px] text-gray-500">Désactive les notifications push sur tous vos appareils.</div>
      </div>
      {loading && <Loader2 size={14} className="animate-spin text-blue-400" />}
    </button>
  );
};

// ─── Appareils sécurisés (WebAuthn) ─────────────────────────────
const AppareilsSecurises: React.FC<{ onFeedback: (t: string) => void }> = ({ onFeedback }) => {
  const [supporte, setSupporte] = useState<boolean | null>(null);
  const [creds, setCreds] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [enregistrement, setEnregistrement] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      const sup = isWebAuthnSupported() && await isPlatformAuthenticatorAvailable();
      setSupporte(sup);
      try {
        const r = await portailApi.webauthnListCredentials();
        setCreds(r.results);
      } catch (e: any) {
        // ignore
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const enregistrer = async () => {
    const nom = window.prompt(
      "Nom pour cet appareil (ex: 'iPhone perso', 'PC portable') :",
      'Mon ' + (/iPhone|iPad/i.test(navigator.userAgent) ? 'iPhone'
        : /Android/i.test(navigator.userAgent) ? 'Android' : 'appareil'),
    );
    if (!nom) return;
    setErr(null);
    setEnregistrement(true);
    try {
      const opts = await portailApi.webauthnRegisterBegin();
      const cred = await navigator.credentials.create({
        publicKey: decodeCreationOptions(opts),
      }) as PublicKeyCredential;
      if (!cred) throw new Error('Inscription annulée');
      const enc = encodeAttestationCredential(cred);
      await portailApi.webauthnRegisterComplete(enc, nom.trim());
      const r = await portailApi.webauthnListCredentials();
      setCreds(r.results);
      onFeedback('Empreinte enregistrée — vous pourrez vous connecter avec.');
    } catch (e: any) {
      const msg = e?.name === 'NotAllowedError'
        ? "Opération refusée ou expirée."
        : (e?.data?.detail || e?.message || 'Erreur d\'enregistrement');
      setErr(msg);
    } finally {
      setEnregistrement(false);
    }
  };

  const supprimer = async (c: any) => {
    if (!window.confirm(`Supprimer « ${c.nom_appareil} » ?\n\nVous ne pourrez plus vous connecter avec cet appareil sans saisir votre mot de passe.`)) return;
    try {
      await portailApi.webauthnDeleteCredential(c.id);
      const r = await portailApi.webauthnListCredentials();
      setCreds(r.results);
      onFeedback('Appareil retiré.');
    } catch (e: any) {
      setErr(e?.message || 'Erreur');
    }
  };

  if (loading) {
    return (
      <div className="px-3 py-4 text-xs text-gray-500">
        <Loader2 size={12} className="inline animate-spin mr-1" /> Vérification…
      </div>
    );
  }
  if (!supporte) {
    return (
      <div className="px-3 py-3 text-xs text-gray-500 leading-relaxed">
        <Fingerprint size={14} className="inline mr-1.5 text-gray-400" />
        Votre navigateur/appareil ne supporte pas l'empreinte (Touch&nbsp;ID,
        empreinte Android, Windows&nbsp;Hello). Utilisez un téléphone récent
        ou Chrome/Safari à jour.
      </div>
    );
  }

  return (
    <div className="px-3 py-3 space-y-3">
      <div className="text-[11px] text-gray-500 leading-relaxed">
        Activez l'empreinte digitale (ou Face ID / Windows Hello) sur les
        appareils que vous utilisez. Une fois activé, vous pourrez vous connecter
        en touchant simplement votre téléphone, sans taper votre mot de passe.
        C'est aussi sûr qu'un mot de passe long&nbsp;: la clé reste dans votre
        appareil et n'est jamais transmise.
      </div>

      {creds.length === 0 ? (
        <div className="text-xs text-orange-400 inline-flex items-center gap-1.5">
          <AlertCircle size={12} /> Aucun appareil enregistré.
        </div>
      ) : (
        <div className="space-y-1.5">
          {creds.map((c) => (
            <div
              key={c.id}
              className="flex items-center gap-2 px-2.5 py-2 bg-gray-950 border border-gray-700 rounded"
            >
              <Fingerprint size={14} className="text-green-400 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-xs font-medium text-gray-100 truncate">
                  {c.nom_appareil}
                </div>
                <div className="text-[10px] text-gray-500">
                  Enregistré le {new Date(c.date_creation).toLocaleDateString('fr-FR')}
                  {c.derniere_utilisation && (
                    <> · utilisé le {new Date(c.derniere_utilisation).toLocaleDateString('fr-FR')}</>
                  )}
                </div>
              </div>
              <button
                onClick={() => supprimer(c)}
                className="p-1 text-gray-500 hover:text-red-400 flex-shrink-0"
                title="Supprimer cet appareil"
              >
                <Trash2 size={13} />
              </button>
            </div>
          ))}
        </div>
      )}

      {err && (
        <div className="text-xs text-red-400 inline-flex items-center gap-1">
          <AlertCircle size={11} /> {err}
        </div>
      )}

      <button
        onClick={enregistrer}
        disabled={enregistrement}
        className="w-full px-3 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium rounded inline-flex items-center justify-center gap-2"
      >
        {enregistrement ? <Loader2 size={14} className="animate-spin" /> : <Fingerprint size={14} />}
        Activer l'empreinte sur cet appareil
      </button>
    </div>
  );
};

// ─── Question de sécurité ───────────────────────────────────────
const QuestionSecurite: React.FC<{ onSaved: () => void }> = ({ onSaved }) => {
  const [open, setOpen] = useState(false);
  const [questionExistante, setQuestionExistante] = useState<string>('');
  const [hasReponse, setHasReponse] = useState(false);
  const [ancien, setAncien] = useState('');
  const [question, setQuestion] = useState('');
  const [reponse, setReponse] = useState('');
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    portailApi.getQuestionSecurite()
      .then((r) => {
        setQuestionExistante(r.question_securite || '');
        setHasReponse(r.has_reponse);
        if (r.question_securite) setQuestion(r.question_securite);
      })
      .catch(() => { /* ignore */ });
  }, []);

  const submit = async () => {
    setErr(null);
    if (question.trim().length < 5) { setErr('Question ≥ 5 caractères'); return; }
    if (reponse.trim().length < 2) { setErr('Réponse ≥ 2 caractères'); return; }
    setLoading(true);
    try {
      await portailApi.setQuestionSecurite({
        ancien_password: ancien, question: question.trim(), reponse: reponse.trim(),
      });
      setAncien(''); setReponse('');
      setHasReponse(true);
      setQuestionExistante(question.trim());
      setOpen(false);
      onSaved();
    } catch (e: any) {
      setErr(e?.data?.detail || e?.message || 'Erreur');
    } finally {
      setLoading(false);
    }
  };

  const configurée = hasReponse && questionExistante;

  return (
    <div className="px-3 py-3">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-3 text-left"
      >
        <Shield size={14} className={configurée ? 'text-green-400' : 'text-orange-400'} />
        <div className="flex-1">
          <div className="text-sm font-medium text-gray-100">
            Question de sécurité {configurée
              ? <span className="text-[10px] text-green-400 ml-1">✓ configurée</span>
              : <span className="text-[10px] text-orange-400 ml-1">⚠ non configurée</span>}
          </div>
          <div className="text-[11px] text-gray-500">
            {configurée
              ? `Question actuelle : « ${questionExistante.length > 60 ? questionExistante.slice(0, 60) + '…' : questionExistante} »`
              : 'Sans elle, vous ne pourrez pas récupérer votre compte en cas d\'oubli.'}
          </div>
        </div>
        <span className="text-xs text-blue-400">{open ? 'Fermer' : 'Configurer'}</span>
      </button>
      {open && (
        <div className="mt-3 ml-6 space-y-2">
          <input
            type="password"
            placeholder="Mot de passe actuel (vérification)"
            value={ancien}
            onChange={(e) => setAncien(e.target.value)}
            className="w-full px-3 py-2 bg-gray-950 border border-gray-700 rounded text-sm text-gray-100 focus:outline-none focus:border-blue-500"
          />
          <input
            type="text"
            placeholder="Question (ex: Surnom de mon meilleur ami du primaire)"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            className="w-full px-3 py-2 bg-gray-950 border border-gray-700 rounded text-sm text-gray-100 focus:outline-none focus:border-blue-500"
          />
          <input
            type="text"
            placeholder="Réponse confidentielle"
            value={reponse}
            onChange={(e) => setReponse(e.target.value)}
            className="w-full px-3 py-2 bg-gray-950 border border-gray-700 rounded text-sm text-gray-100 focus:outline-none focus:border-blue-500"
          />
          <div className="text-[10px] text-gray-500">
            ⚠ Pas la date de naissance ni le nom de votre option. Mémorisez cette réponse — elle sera demandée à chaque réinitialisation.
          </div>
          {err && (
            <div className="text-xs text-red-400 inline-flex items-center gap-1">
              <AlertCircle size={11} /> {err}
            </div>
          )}
          <button
            onClick={submit}
            disabled={loading || !ancien || !question || !reponse}
            className="w-full px-3 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium rounded inline-flex items-center justify-center gap-2"
          >
            {loading ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
            Enregistrer
          </button>
        </div>
      )}
    </div>
  );
};
